import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { TableContainer, Typography, Box, ThemeProvider, Button, TextField, Tooltip, IconButton } from "@mui/material";
import { VariableSizeList } from "react-window";
import { IniInfo } from "system/types/interfaces";
import { usePolicyEditorPermissions } from "system/hooks";
import { RootState } from "system/store";
import { useDispatch, useSelector } from "react-redux";
import { policyEditorActions } from "system/store/slices/policy-editor-slice";
import { muiTheme } from "system/styles/muiTheme";
import { Icon } from "@fluentui/react/lib/Icon";
import { AddSystemConfigModal } from "./AddSystemConfigModal";
import { SystemConfigRow } from "./SystemConfigRow";
import InvalidRulesModal from "../applicabilityRulesTab/InvalidRulesModal";
import PendingChangesModal from "../applicabilityRulesTab/PendingChangesModal";
import { useDebouncedCallback } from "use-debounce";
import { isRowDirty, restoreOriginalCommentFields, restoreOriginalUncommentFields } from "components/modules/policy-editor/utils/ruleSignature";

const SystemConfigTab = () => {
  const dispatch = useDispatch();
  const { isReadOnly: readOnlyViewer } = usePolicyEditorPermissions();
  const systemConfigurations = useSelector((state: RootState) => state.policyEditor.systemConfigurations);
  const invalidConfigs = useSelector((state: RootState) => state.policyEditor.invalidConfigs);
  const [isModalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<"add" | "edit">("add");
  const [insertIndex, setInsertIndex] = useState<number | null>(null);
  const [isErrorModalOpen, setErrorModalOpen] = useState(false);
  const [isPendingChangesModalOpen, setPendingChangesModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState<number[]>([]);
  const [currentResultIndex, setCurrentResultIndex] = useState(-1);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<VariableSizeList>(null);
  const [draftConfigRule, setDraftConfigRule] = useState<IniInfo | null>(null);
  const [draftComment, setDraftComment] = useState<string | null>(null);

  const sensors = useSensors(useSensor(PointerSensor), useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));

  const handleSearch = useDebouncedCallback((term: string) => {
    if (!term) {
      setSearchResults([]);
      setCurrentResultIndex(-1);
      return;
    }
    const results = systemConfigurations
      .map((config, index) => {
        const isComment = config.isComment ?? false;
        const searchInName = config.name.toLowerCase().includes(term.toLowerCase());
        const searchInComment = isComment && config.value && config.value.toLowerCase().includes(term.toLowerCase());
        const searchInDelimiterValue =
          !isComment &&
          `${config.delimiter} ${config.value}${config.inlineComment ? `;${config.inlineComment}` : ""}`
            .toLowerCase()
            .includes(term.toLowerCase());
        return searchInName || searchInComment || searchInDelimiterValue ? index : -1;
      })
      .filter((index) => index !== -1);
    setSearchResults(results);
    setCurrentResultIndex(results.length > 0 ? 0 : -1);
    if (results.length > 0) {
      listRef.current?.scrollToItem(results[0], "center");
    }
  }, 800);

  useEffect(() => {
    setTimeout(() => {
      if (searchInputRef.current) {
        searchInputRef.current.querySelector("input")?.focus();
      }
    }, 1000);
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (searchResults.length === 0) return;
      switch (e.key) {
        case "Enter":
          e.preventDefault();
          const nextIndex = (currentResultIndex + 1) % searchResults.length;
          setCurrentResultIndex(nextIndex);
          listRef.current?.scrollToItem(searchResults[nextIndex], "center");
          break;
        case "ArrowUp":
          e.preventDefault();
          const prevIndex = (currentResultIndex - 1 + searchResults.length) % searchResults.length;
          setCurrentResultIndex(prevIndex);
          listRef.current?.scrollToItem(searchResults[prevIndex], "center");
          break;
        case "ArrowDown":
          e.preventDefault();
          const nextIndexDown = (currentResultIndex + 1) % searchResults.length;
          setCurrentResultIndex(nextIndexDown);
          listRef.current?.scrollToItem(searchResults[nextIndexDown], "center");
          break;
      }
    },
    [currentResultIndex, searchResults]
  );

  const handleReorderRows = useCallback(
    (oldIndex: number, newIndex: number) => {
      if (oldIndex === newIndex) return;

      const adjustedNewIndex = Math.max(0, Math.min(newIndex, systemConfigurations.length - 1));

      dispatch(policyEditorActions.reorderSystemConfigs({ oldIndex, newIndex: adjustedNewIndex }));

      listRef.current?.scrollToItem(adjustedNewIndex, "center");
    },
    [dispatch, systemConfigurations.length]
  );

  const handleDragEnd = useCallback(
    (event: any) => {
      const { active, over } = event;

      if (active.id !== over.id) {
        const oldIndex = systemConfigurations.findIndex((config) => config.id === active.id);
        const newIndex = systemConfigurations.findIndex((config) => config.id === over.id);
        handleReorderRows(oldIndex, newIndex);
      }
    },
    [handleReorderRows, systemConfigurations]
  );

  const handleAddLineClick = useCallback((index: number) => {
    const draft: IniInfo = {
      id: Date.now().toString(),
      name: "",
      value: "",
      applicability: "",
      delimiter: "=",
      pendingChanges: true,
    };

    setDraftConfigRule(draft);

    setInsertIndex(index);
    setModalOpen(true);
    setModalMode("add");
  }, []);

  const handleModalClose = useCallback(() => {
    setModalMode("add");
    setInsertIndex(null);
    setModalOpen(false);
    setDraftConfigRule(null);
  }, []);

  const checkForDuplicateRule = useCallback(
    (newRule: IniInfo, currentId: string | null): boolean => {
      const newRuleString = `${newRule.name} ${newRule.delimiter} ${newRule.applicability} ${newRule.value}`.trim();
      return systemConfigurations.some((existingRule) => {
        if (currentId && existingRule.id === currentId) return false;
        if (existingRule.isDeleted) return false;
        const existingRuleString =
          `${existingRule.name} ${existingRule.delimiter} ${existingRule.applicability} ${existingRule.value}`.trim();
        return existingRuleString === newRuleString;
      });
    },
    [systemConfigurations]
  );

  const handleModalSubmit = useCallback(
    (data: IniInfo, type: "config" | "comment") => {
      if (insertIndex === null) return;
      if (checkForDuplicateRule(data, data.id ?? "")) {
        return `The rule "${data.name}" with value "${data.value}" and applicability "${data.delimiter} ${data.applicability}" already exists.`;
      }

      const existingIndex = systemConfigurations.findIndex((x) => x.id === data.id);

      if (existingIndex >= 0) {
        dispatch(policyEditorActions.updateSystemConfig({ index: existingIndex, updatedConfig: data }));
      } else {
        const insertPosition = insertIndex ?? systemConfigurations.length;
        dispatch(policyEditorActions.addSystemConfig({ index: insertPosition, config: data }));
      }

      handleModalClose();
    },
    [checkForDuplicateRule, dispatch, handleModalClose, insertIndex, systemConfigurations]
  );

  const handleDeleteRow = useCallback(
    (index: number) => {
      dispatch(policyEditorActions.deleteSystemConfig(index));
    },
    [dispatch]
  );

  const handleUndoDelete = useCallback(
    (index: number) => {
      dispatch(policyEditorActions.undoDeleteSystemConfig(index));
    },
    [dispatch]
  );

  const handleEditConfig = useCallback((config: IniInfo, index: number, isComment: boolean = false) => {
    if (!isComment) {
      setDraftConfigRule(config);
    } else {
      setDraftComment(config.value || "");
    }
    setInsertIndex(index);
    setModalOpen(true);
    setErrorModalOpen(false);  
    setModalMode("edit");
  }, []);

  const handleToggleComment = useCallback(
    (id: string, newContent: string, isComment: boolean) => {
      const updatedConfigs = systemConfigurations.map((config: IniInfo) => {
        if (config.id !== id) return config;
        if (isComment) {
          const restored = restoreOriginalCommentFields(config);
          if (restored) return restored;
          return {
            ...config,
            isComment: true,
            value: newContent,
            name: `New-Comment-${Date.now()}`,
            delimiter: "",
            inlineComment: "",
          };
        } else {
          // Uncommenting: if the row was originally NOT a comment, restore the load-time fields
          // so the signature matches the original and the row drops out of pendingChanges. AB#3115.
          const restored = restoreOriginalUncommentFields(config);
          if (restored) return restored;
          // Otherwise (line added during this session) parse rule.value back into fields.
          const uncommentedContent = config.value?.replace(/^;/, "").trim() || "";
          const validDelimiters = [" is for any ", " is ", " = "];
          let name = uncommentedContent;
          let delimiter = "";
          let value = "";
          let inlineComment = "";

          for (const d of validDelimiters) {
            const parts = uncommentedContent.split(new RegExp(`\\s*${d}\\s*`, "i"));
            if (parts.length >= 2) {
              name = parts[0].trim();
              delimiter = d;
              const afterDelimiter = parts.slice(1).join(d).trim();
              const commentParts = afterDelimiter.split(/;(.+)/);
              if (commentParts.length > 1) {
                value = commentParts[0].trim();
                inlineComment = commentParts[1].trim();
              } else {
                value = afterDelimiter;
                inlineComment = "";
              }
              break;
            }
          }

          if (!delimiter) {
            name = uncommentedContent;
            const commentParts = uncommentedContent.split(/;(.+)/);
            if (commentParts.length > 1) {
              name = commentParts[0].trim();
              inlineComment = commentParts[1].trim();
            }
          }

          return {
            ...config,
            isComment: false,
            name,
            delimiter,
            value,
            inlineComment,
          };
        }
      });
      const withDirt = updatedConfigs.map((c) =>
        c.id === id ? { ...c, pendingChanges: isRowDirty(c, updatedConfigs) } : c
      );
      dispatch(policyEditorActions.setSystemConfigurations(withDirt));
    },
    [systemConfigurations, dispatch]
  );

  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "/") {
        e.preventDefault();
        if (searchInputRef.current) {
          searchInputRef.current.querySelector("input")?.focus();
        }
      }
    };
    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, []);

  const modifiedConfigs = useMemo(() => {
    return systemConfigurations.map((rule, index) => ({ index, rule })).filter(({ rule }) => rule.pendingChanges);
  }, [systemConfigurations]);

  const Row = React.memo(({ index, style }: { index: number; style: React.CSSProperties }) => {
    const config = systemConfigurations[index];
    return (
      <SystemConfigRow
        key={config.id}
        id={config.id!}
        config={config}
        index={index}
        style={style}
        onAdd={handleAddLineClick}
        onDelete={handleDeleteRow}
        onUndoDelete={handleUndoDelete}
        onEdit={handleEditConfig}
        searchTerm={searchTerm}
        onReorder={handleReorderRows}
        totalRows={systemConfigurations.length}
        onToggleComment={handleToggleComment}
        readOnlyViewer={readOnlyViewer}
      />
    );
  });

  return (
    <ThemeProvider theme={muiTheme}>
      <Box sx={{ color: "text.primary" }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box display="flex" gap={1} alignItems={"center"}>
            <Tooltip title="Press Enter to navigate through the results" arrow>
              <TextField
                ref={searchInputRef}
                placeholder="Search System Configurations..."
                variant="outlined"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  handleSearch(e.target.value);
                }}
                onKeyDown={handleKeyDown}
                sx={{ marginRight: 2, width: "350px" }}
                InputProps={{
                  endAdornment: searchResults.length > 0 && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0 }}>
                      <Typography
                        variant="subtitle2"
                        sx={{ padding: "0 8px", color: "#fff", opacity: 0.7, borderRight: "1px solid #cccccc" }}
                      >
                        {currentResultIndex + 1}/{searchResults.length}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => {
                          const prevIndex = (currentResultIndex - 1 + searchResults.length) % searchResults.length;
                          setCurrentResultIndex(prevIndex);
                          listRef.current?.scrollToItem(searchResults[prevIndex], "center");
                        }}
                        sx={{ color: "#fff", opacity: 0.7, "&:hover": { color: "#4caf50", opacity: 1 } }}
                      >
                        <Icon iconName="ChevronUp" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => {
                          const nextIndex = (currentResultIndex + 1) % searchResults.length;
                          setCurrentResultIndex(nextIndex);
                          listRef.current?.scrollToItem(searchResults[nextIndex], "center");
                        }}
                        sx={{ color: "#fff", opacity: 0.7, "&:hover": { color: "#4caf50", opacity: 1 } }}
                      >
                        <Icon iconName="ChevronDown" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => {
                          setSearchTerm("");
                          handleSearch("");
                        }}
                        sx={{ fontSize: "18px", color: "#fff", opacity: 0.7, "&:hover": { color: "#4caf50", opacity: 1 } }}
                      >
                        <Icon iconName="Cancel" style={{ fontSize: "inherit" }} />
                      </IconButton>
                    </Box>
                  ),
                }}
              />
            </Tooltip>
          </Box>
          <Box display="flex" gap={1} alignItems={"center"}>
            {invalidConfigs.length > 0 && (
              <Button
                variant="text"
                sx={{ backgroundColor: "#d5878b", color: "#000", "&:hover": { backgroundColor: "#ca6469" }, textTransform: "none" }}
                onClick={() => setErrorModalOpen(true)}
              >
                <b style={{ marginRight: "3px" }}>{invalidConfigs.length}</b> {invalidConfigs.length === 1 ? "error" : "errors"} found
              </Button>
            )}
            {modifiedConfigs.length > 0 && (
              <Button
                variant="text"
                sx={{ backgroundColor: "#fbc02d", color: "#000", "&:hover": { backgroundColor: "#e3ac27" }, textTransform: "none" }}
                onClick={() => setPendingChangesModalOpen(true)}
              >
                <b style={{ marginRight: "3px" }}>{modifiedConfigs.length}</b> Pending {modifiedConfigs.length === 1 ? "Change" : "Changes"}
              </Button>
            )}
          </Box>
        </Box>

        <TableContainer
          sx={{
            maxHeight: "85vh",
            border: "1px solid #333",
            backgroundColor: "#1b1a19",
            borderRadius: "8px",
            overflowY: "auto",
            overflowX: "hidden",
          }}
          component="div"
        >
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
            onDragStart={() => {
              setSearchTerm("");
              setSearchResults([]);
              setCurrentResultIndex(-1);
            }}
          >
            <SortableContext items={systemConfigurations.filter((config) => config.id).map((config) => config.id!)} strategy={verticalListSortingStrategy}>
              <VariableSizeList
                height={window.innerHeight - 200}
                width="100%"
                itemCount={systemConfigurations.length}
                itemSize={() => 50}
                ref={listRef}
              >
                {Row}
              </VariableSizeList>
            </SortableContext>
          </DndContext>
        </TableContainer>
      </Box>

      <AddSystemConfigModal
        open={isModalOpen}
        onClose={handleModalClose}
        onSubmit={handleModalSubmit}
        initialRule={draftConfigRule}
        initialComment={draftComment}
        mode={modalMode}
      />

      <InvalidRulesModal
        open={isErrorModalOpen}
        onClose={() => setErrorModalOpen(false)}
        invalidRules={invalidConfigs}
        onNavigate={handleEditConfig}
      />
      <PendingChangesModal
        open={isPendingChangesModalOpen}
        onClose={() => setPendingChangesModalOpen(false)}
        modifiedRules={modifiedConfigs}
        onNavigate={handleEditConfig}
      />
    </ThemeProvider>
  );
};

export default SystemConfigTab;
